/**
 * Media event types for real-time notifications (phase-8a seam, GAP-C bridge).
 *
 * Mirror of the storage-events pattern (storage-events.enum.ts): media and
 * the encoding worker publish these JSON payloads to the Redis pub/sub
 * channel `media:events`; the gateway's `WsMediaEventsBridge` subscribes and
 * fans them out to the org-scoped `media:updates:{orgId}` Socket.io room
 * (master A13 — see room-registry.ts ORG_SCOPED_REALTIME_ROOMS).
 *
 * The eight lifecycle members below mirror the "catalog v12" delta registered
 * in `events/catalog/media.ts` (P4-T06, `since: '1.18.0'`); the three phase-8b
 * members mirror the "catalog v13" delta (P5-T06, `since: '1.19.0'`). Every
 * catalog `media:*` name has an enum member so client-side maps can use
 * `Object.values(MediaEventType)` without drift.
 */
export enum MediaEventType {
  ASSET_REGISTERED = 'media:asset-registered',
  JOB_QUEUED = 'media:job-queued',
  JOB_PROGRESS = 'media:job-progress',
  JOB_COMPLETED = 'media:job-completed',
  JOB_FAILED = 'media:job-failed',
  // Catalog v12 completion (P4-T06).
  TITLE_PUBLISHED = 'media:title-published',
  TITLE_SCHEDULED = 'media:title-scheduled',
  TITLE_HIDDEN = 'media:title-hidden',
  TITLE_ARCHIVED = 'media:title-archived',
  ASSET_PROBED = 'media:asset-probed',
  ASSET_READY = 'media:asset-ready',
  JOB_CANCELLED = 'media:job-cancelled',
  SUBTITLE_IMPORTED = 'media:subtitle-imported',
  // Catalog v13 / phase-8b additions (P5-T06).
  PACKAGING_COMPLETED = 'media:packaging-completed',
  PACKAGING_FAILED = 'media:packaging-failed',
  PLAYBACK_TOKEN_ISSUED = 'media:playback-token-issued',
}

/**
 * Wire payload for the `media:events` Redis channel.
 *
 * `orgId` is REQUIRED for room routing (the bridge refuses to fan out an
 * org-scoped event whose tenant cannot be verified — fail-closed, never a
 * cross-tenant broadcast). `assetId`/`jobId` are the aggregate references.
 */
export interface MediaEvent {
  type: MediaEventType | string;
  orgId: string;
  assetId?: string;
  jobId?: string;
  data: Record<string, unknown>;
  timestamp: string;
}

/**
 * Pub/sub channel for media events.
 */
export const MEDIA_EVENTS_CHANNEL = 'media:events';

/**
 * Base WebSocket room for media updates. Fan-out and joins append the
 * verified tenant: the live Socket.io room is `media:updates:{orgId}`
 * (ROOM_MEDIA_UPDATES in events/room-registry.ts is ORG_SCOPED per master
 * A13; WsMediaEventsBridge derives the scoped room from each event's orgId).
 */
export const MEDIA_UPDATES_ROOM = 'media:updates';
